import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase/client";
import type { Database } from "@/types/database";

export type AppRole = Database["public"]["Enums"]["app_role"];

export type Profile = {
  id: string;
  organization_id: string;
  full_name: string;
  short_name: string | null;
  email: string | null;
  is_active: boolean;
  must_change_password: boolean;
  password_changed_at: string | null;
};

export type AuthState = {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  loading: boolean;
  aal: "aal1" | "aal2" | null;
  needsMfaSetup: boolean;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [aal, setAal] = useState<"aal1" | "aal2" | null>(null);
  const [needsMfaSetup, setNeedsMfaSetup] = useState(false);

  const computeAal = useCallback(async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (data) {
      setAal(data.currentLevel as "aal1" | "aal2");
      // Se o user tem factor enrolado mas não verificou na sessão actual, precisamos do challenge
      setNeedsMfaSetup(data.currentLevel === "aal1" && data.nextLevel === "aal1");
    }
  }, []);

  const loadProfile = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("id, organization_id, full_name, short_name, email, is_active, must_change_password, password_changed_at")
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      console.error("loadProfile error:", error);
      setProfile(null);
      return;
    }
    setProfile(data as Profile | null);
  }, []);

  const loadRoles = useCallback(async (userId: string) => {
    const { data, error } = await supabase
      .from("practitioner_roles")
      .select("role")
      .eq("profile_id", userId)
      .eq("is_active", true);
    if (error) {
      console.error("loadRoles error:", error);
      setRoles([]);
      return;
    }
    const unique = Array.from(
      new Set((data ?? []).map((r) => r.role as AppRole)),
    );
    setRoles(unique);
  }, []);

  useEffect(() => {
    // Estado inicial
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session) {
        void loadProfile(data.session.user.id);
        void loadRoles(data.session.user.id);
        void computeAal();
      }
      setLoading(false);
    });

    // Subscrever alterações
    const { data: subscription } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession);
      if (newSession) {
        void loadProfile(newSession.user.id);
        void loadRoles(newSession.user.id);
        void computeAal();
      } else {
        setProfile(null);
        setRoles([]);
        setAal(null);
        setNeedsMfaSetup(false);
      }
      if (event === "SIGNED_OUT") {
        setProfile(null);
        setRoles([]);
        setAal(null);
      }
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, [loadProfile, loadRoles, computeAal]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshProfile = useCallback(async () => {
    if (session?.user) {
      await loadProfile(session.user.id);
      await loadRoles(session.user.id);
    }
    await computeAal();
  }, [session, loadProfile, loadRoles, computeAal]);

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        session,
        profile,
        roles,
        loading,
        aal,
        needsMfaSetup,
        signOut,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve ser usado dentro de <AuthProvider>");
  return ctx;
}
